import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { style } from '../../../components/pdf/fontstyles';
import { AutoText } from '../../../components/pdf/autoText';

// Stylesheet
const styles = StyleSheet.create({
  table: {
    marginTop: 10,
    display: 'table',
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
  },
  row: {
    flexDirection: 'row',
    borderBottom: '1px solid #000',
  },
  cell: {
    padding: 5,
    borderRight: '1px solid #000',
  },
  headerCell: {
    fontWeight: 'bold',
    backgroundColor: '#f2f2f2',
  },
  table1: { width: '25%' },
  table2: { width: '16.67%' },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
    marginTop: 20
  },
  subSectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 5,
    marginTop: 5
  },
  calculationRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  indentedRow: {
    marginLeft: 15,
    marginBottom: 4,
  },
  calculationLabel: {
    marginTop: 2,
    fontSize: 10,
    flex: 2,
  },
  calculationValue: {
    fontSize: 10,
    fontWeight: 'bold',
    flex: 1,
    textAlign: 'right',
  },
  calculationNote: {
    fontSize: 9,
    marginLeft: 10,
  }
});

const RoughCalculation = ({ result }) => {
  // Ensure result has required properties
  if (!result || !result.dateOfRetirement || !result.payrateTable || !result.calculation || !result.calculation[0]) {
    console.error("Missing required data in RoughCalculation component");
    return (
      <View style={style.page}>
        <Text style={[style.title, style.ArialBlack]}>
          Error: Missing required data for calculation
        </Text>
      </View>
    );
  }

  // Safely access properties
  const payrateTable = result.payrateTable || [];
  const calculation = result.calculation[0] || {};
  const PensionablePayAvg = calculation.PensionablePayAvg || {};
  
  // Date calculations
  const [d, m, y] = result.dateOfRetirement.split('/').map(Number);
  const retirementDate = new Date(y, m - 1, d);
  
  const startEnhanced = new Date(retirementDate);
  startEnhanced.setDate(startEnhanced.getDate() + 1);
  
  const endEnhanced = new Date(retirementDate);
  endEnhanced.setFullYear(endEnhanced.getFullYear() + 10);
  
  const formatDate = (date) => 
    `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;

  // Formatted dates
  const enhancedStart = formatDate(startEnhanced);
  const enhancedEnd = formatDate(endEnhanced);
  const normalStart = formatDate(new Date(endEnhanced.setDate(endEnhanced.getDate() + 1)));

  // Table data
  const personalDetailsTable = [
    [result.dateOfBirth || '--', result.dateOfAppointment || '--', result.dateOfRetirement || '--', result.dateOfService || '--'],
  ];
  
  const pensionableServiceTable = [
    [
      calculation.pensionableService?.years || 0,
      calculation.pensionableService?.months || 0,
      calculation.pensionableService?.days || 0,
      calculation.terms || 0
    ],
  ];
  
  // Safe access to payrateTable elements
  const pensionablePayTable = [
    [
      payrateTable[0]?.from || '--',
      payrateTable[0]?.to || '--',
      `${PensionablePayAvg.period1?.months || 0} M ${(PensionablePayAvg.period1?.Dfirst || 0) + (PensionablePayAvg.period1?.Dsecond || 0)} D`,
      payrateTable[0]?.payrate || 0,
      PensionablePayAvg.total1 || 0,
      '-'
    ],
    [
      payrateTable[1]?.from || '--',
      payrateTable[1]?.to || '--',
      `${PensionablePayAvg.period2?.months || 0} M ${(PensionablePayAvg.period2?.Dfirst || 0) + (PensionablePayAvg.period1?.Dsecond || 0)} D`,
      payrateTable[1]?.payrate || 0,
      PensionablePayAvg.total2 || 0,
      '-'
    ],
    [
      payrateTable[0]?.from || '--',
      payrateTable[1]?.to || payrateTable[0]?.to || '--',
      '10 Months',
      '--',
      PensionablePayAvg.netTotal || 0,
      `Avg. ${Math.round((PensionablePayAvg.netTotal || 0) / 10)}`
    ],
  ];

  // Calculations
  const gratuityAmount = Math.round(result.basicPay * result.calculation[0].gratuityFactor);
  const cappedGratuity = Math.min(2000000, gratuityAmount);
  const residualPension = result.pension - result.commutedPension;
  const commutedValue = Math.round(result.commutedPension * 12 * result.calculation[0].commutationFactor);
  const enhancedFamilyPension = Math.round(result.basicPay * 0.5);
  const normalFamilyPension = Math.round(result.basicPay * 0.3);

  return (
    <View style={style.page}>
      {/* Header Section */}
      <Text style={[style.title, style.ArialBlack]}>
        Rough Calculation of Pension, Gratuity and Commutation Value
      </Text>
      <View style={style.solidLine}></View>

      {/* Employee Information */}
      <AutoText style={[style.paragraph, { marginBottom: 0 }]}>
        Name of the Office : {result.institutionName}
      </AutoText>
      <AutoText style={[style.paragraph, { marginTop: 1 }]}>
        Name of the Employee/Pensioner : {result.employeeName}  Designation : {result.designation} 
      </AutoText>
      
      {/* Personal Details Table */}
      <View style={[styles.table, { marginTop: 0 }]}>
        <View style={[styles.row, styles.headerCell]}>
          <Text style={[styles.cell, styles.table1]}>Date of Birth</Text>
          <Text style={[styles.cell, styles.table1]}>Date of Appointment</Text>
          <Text style={[styles.cell, styles.table1]}>Date of Retirement</Text>
          <Text style={[styles.cell, styles.table1]}>Date of Confirmation</Text>
        </View>
        {personalDetailsTable.map((row, index) => (
          <View style={styles.row} key={index}>
            {row.map((cell, i) => (
              <Text style={[styles.cell, styles.table1]} key={i}>{cell}</Text>
            ))}
          </View>
        ))}
      </View>

      {/* Pensionable Service */}
      <Text style={styles.subSectionTitle}>1) Pensionable Service</Text>
      <View style={[styles.table, { marginTop: 0 }]}>
        <View style={[styles.row, styles.headerCell]}>
          <Text style={[styles.cell, styles.table1]}>Years</Text>
          <Text style={[styles.cell, styles.table1]}>Months</Text>
          <Text style={[styles.cell, styles.table1]}>Days</Text>
          <Text style={[styles.cell, styles.table1]}>Terms</Text>
        </View>
        {pensionableServiceTable.map((row, index) => (
          <View style={styles.row} key={index}>
            {row.map((cell, i) => (
              <Text style={[styles.cell, styles.table1]} key={i}>{cell}</Text>
            ))}
          </View>
        ))}
      </View>

      {/* Pensionable Pay */}
      <Text style={styles.subSectionTitle}>2) Calculation of Pensionable Pay</Text>
      <Text style={style.paragraph}>A) Last 10 Months Average Pay</Text>
      <View style={[styles.table, { marginTop: 0 }]}>
        <View style={[styles.row, styles.headerCell]}>
          <Text style={[styles.cell, styles.table2]}>From</Text>
          <Text style={[styles.cell, styles.table2]}>To</Text>
          <Text style={[styles.cell, styles.table2]}>Months</Text>
          <Text style={[styles.cell, styles.table2]}>Pay Rate</Text>
          <Text style={[styles.cell, styles.table2]}>Amount</Text>
          <Text style={[styles.cell, styles.table2]}>Average Pay</Text>
        </View>
        {pensionablePayTable.map((row, index) => (
          <View style={styles.row} key={index}>
            {row.map((cell, i) => (
              <Text style={[styles.cell, styles.table2]} key={i}>{cell}</Text>
            ))}
          </View>
        ))}
      </View>

      <Text style={[style.paragraph, { marginTop: 10 }]}>
        B) Last Month's Pay : {result.basicPay}
      </Text>
      <Text style={[style.paragraph]}>
        C) Notional Pay : {result.notionalPay}
      </Text>
      <Text style={styles.paragraph}>
        D) Pensionable Pay (max of A and B): {result.pensionablePay || 0}
      </Text>

      {/* Pension Calculation */}
      <Text style={[styles.paragraph, {marginTop:5}]}>
        3) Pension Calculation:
      </Text>
      <View style={styles.table}>

        <View style={[styles.row, styles.headerCell]}>
          <Text style={[styles.cell, { flex: 2 }]}>Pension: </Text>
          <AutoText style={[styles.cell, { flex: 1 }]}>Amount (₹)</AutoText>
        </View>
        <View style={styles.row}>
          <Text style={[styles.cell, { flex: 2 }]}>Pension = 50% of Pensionable Pay</Text>
          <Text style={[styles.cell, { flex: 1 }]}>{result.pension || 0}</Text>
        </View>
      </View>

      {/* Gratuity Calculation */}
      <Text style={[styles.paragraph, {marginTop:10}]}>
        4) Death /Retirement Gratuity:
      </Text>
      <View style={[styles.table]}>
        <View style={[styles.row, styles.headerCell]}>
          <Text style={[styles.cell, { flex: 2 }]}>Service Gratuity or DCRG:</Text>
          <AutoText style={[styles.cell, { flex: 1 }]}>Amount (₹)</AutoText>
        </View>
        <View style={styles.row}>
          <Text style={[styles.cell, { flex: 2 }]}>
            Last pay * Terms/4 = {result.basicPay} × {result.calculation[0].gratuityFactor}
          </Text>
          <Text style={[styles.cell, { flex: 1 }]}>
            {gratuityAmount}
          </Text>
        </View>
        <View style={styles.row}>
          <AutoText style={[styles.cell, { flex: 2 }]}>Gratuity Capped at Maximum (₹20,00,000)</AutoText>
          <Text style={[styles.cell, { flex: 1 }]}>{cappedGratuity}</Text>
        </View>
      </View>

    </View>

  );
};

export default RoughCalculation;